import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { showError, showSuccess } from '@/utils/toast';
import { getCsrfToken } from '@/utils/csrf';
import { Video } from 'lucide-react';

interface SuggestVideoModalProps {
    isOpen: boolean;
    onClose: () => void;
    gameId: number;
}

const SuggestVideoModal: React.FC<SuggestVideoModalProps> = ({ isOpen, onClose, gameId }) => {
    const [videoUrl, setVideoUrl] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleSubmit = async () => {
        if (!videoUrl.trim()) {
            showError("Kérlek, adj meg egy YouTube URL-t!");
            return;
        }

        // Egyszerű YouTube URL validáció
        const youtubeRegex = /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.?be)\/.+$/;
        if (!youtubeRegex.test(videoUrl)) {
            showError("Érvénytelen YouTube URL. Kérlek, ellenőrizd a linket!");
            return;
        }

        setIsSubmitting(true);
        try {
            const response = await fetch('/api/youtube_videos/suggest', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-Token': getCsrfToken(),
                },
                body: JSON.stringify({
                    game_id: gameId,
                    link: videoUrl,
                }),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.errors?.join(', ') || 'Ismeretlen hiba történt.');
            }

            showSuccess('Videóajánlásodat rögzítettük! Köszönjük!');
            setVideoUrl('');
            onClose();
        } catch (error) {
            showError(error instanceof Error ? error.message : 'Hiba a videó ajánlása közben.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-[425px] bg-card">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2"><Video className="text-primary" /> Videó ajánlása</DialogTitle>
                    <DialogDescription>
                        Találtál egy jó videót a játékhoz? Küldd be nekünk a linket! Az adminisztrátorok ellenőrzés után közzéteszik.
                        <br /><strong className="text-primary">Kérjük, hogy csak a gépi fordítással készült játékmenetet bemutató videókat küldj be!</strong>
                    </DialogDescription>
                </DialogHeader>
                <div className="grid gap-4 py-4">
                    <Input id="video-url" placeholder="https://www.youtube.com/watch?v=..." value={videoUrl} onChange={(e) => setVideoUrl(e.target.value)} disabled={isSubmitting} />
                </div>
                <DialogFooter>
                    <Button onClick={handleSubmit} disabled={isSubmitting}>{isSubmitting ? 'Küldés...' : 'Ajánlás beküldése'}</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default SuggestVideoModal;